/**
 * Utility functions for displaying user information consistently across components
 */

export interface UserAuthor {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

/**
 * Get display name for a user with fallback to user ID
 */
export const getDisplayName = (author?: UserAuthor, fallbackUserId?: string): string => {
  if (author?.name) {
    return author.name;
  }
  
  if (author?.email) {
    // Extract name part from email as fallback
    return author.email.split('@')[0];
  }
  
  // Fallback to user ID but make it more readable
  if (fallbackUserId) {
    // Take the part after a provider prefix (e.g. "auth0|abc") and, for
    // long opaque subs (e.g. authentik's 64-char hex), show only the last
    // 8 chars so the label stays short but distinguishable.
    const token = fallbackUserId.includes('|')
      ? fallbackUserId.split('|').pop() || fallbackUserId
      : fallbackUserId;
    return token.length > 12 ? `User ${token.slice(-8)}` : token;
  }

  return 'Unknown User';
};

/**
 * Get tooltip text with user details for hover
 */
export const getUserTooltip = (author?: UserAuthor, fallbackUserId?: string): string => {
  const parts: string[] = [];
  
  if (author?.name) {
    parts.push(`Name: ${author.name}`);
  }
  
  if (author?.email) {
    parts.push(`Email: ${author.email}`);
  }
  
  // Always show the Auth0 ID for debugging/admin purposes
  const userId = author?.auth0_user_id || fallbackUserId;
  if (userId) {
    parts.push(`ID: ${userId}`);
  }
  
  return parts.length > 0 ? parts.join('\n') : 'No user details available';
};

/**
 * Get user avatar initials from name or email
 */
export const getUserInitials = (author?: UserAuthor): string => {
  if (author?.name) {
    const nameParts = author.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return author.name[0]?.toUpperCase() || 'U';
  }
  
  if (author?.email) {
    return author.email[0].toUpperCase();
  }
  
  return 'U';
};